import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  forwardRef,
} from 'react';
import classnames from 'clsx';
import styles from '../button.scss';
import { TestIds } from '../../../../constants';
import StylesContext from '../../../StylesContext';
import type { TextSizeBlockType } from './utils/utils';
import {
  textSizeBlockTypes,
  getActiveTextSize,
  getStateWithNewTextSize,
} from './utils/utils';
import type { ExtendedToolbarChildrenProps } from '../../types';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';
import { usePopper } from '../../../../../../providers/usePopper/usePopper';
import Tooltip from '../Tooltip';
import { useActionTranslations } from '../../../useActionTranslations';
import { calculateNextIndex, calculatePreviousIndex } from '../../utils';

export const textSizeButtons: Record<TextSizeBlockType, string> = {
  unstyled: 'Normal Text',
  'header-two': 'Title',
  'header-three': 'Subtitle',
};

const StaticTextSizeButton = forwardRef<
  HTMLButtonElement,
  ExtendedToolbarChildrenProps
>((props, ref) => {
  const [showOptions, setShowOptions] = useState(false);
  const stylesContext = useContext(StylesContext);
  const { setEditorState, getEditorState, rootId, translations, tabIndex } =
    props;
  const { toolbarNormalTextAction, toolbarTitleAction, toolbarSubtitleAction } =
    useActionTranslations(translations);

  const textSizeActions: Record<TextSizeBlockType, string> = {
    unstyled: toolbarNormalTextAction,
    'header-two': toolbarTitleAction,
    'header-three': toolbarSubtitleAction,
  };

  const textSizesSelected: Record<TextSizeBlockType, string> = {
    unstyled: translations.textSizeNormalTextSelected,
    'header-two': translations.textSizeTitleSelected,
    'header-three': translations.textSizeSubtitleSelected,
  };
  const menuRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const textSizeRefs = useRef<
    Record<TextSizeBlockType, React.RefObject<HTMLButtonElement>>
  >({
    unstyled: useRef<HTMLButtonElement>(null),
    'header-two': useRef<HTMLButtonElement>(null),
    'header-three': useRef<HTMLButtonElement>(null),
  });

  const {
    ref: popperSourceElem,
    setRef: setPopperSourceElem,
    setPopper: setPopperTargetElem,
    styles: stylesPopper,
    attributes,
  } = usePopper<HTMLButtonElement>({
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -25],
        },
      },
    ],
  });

  const hideOptions = () => {
    setShowOptions(false);
  };

  useClickOutside([popperSourceElem], hideOptions);

  const changeTextSize = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newTextSize: string,
  ) => {
    e.stopPropagation();
    const newState = getStateWithNewTextSize(newTextSize, getEditorState());
    setEditorState(newState);
    hideOptions();
    popperSourceElem?.focus();
  };

  const onClickHandler = (event: React.MouseEvent<any>) => {
    if (event.currentTarget === popperSourceElem) {
      setShowOptions(!showOptions);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!showOptions) {
      return;
    }

    const buttons = textSizeBlockTypes.map(
      type => textSizeRefs.current[type].current as HTMLButtonElement,
    );

    if (event.key === 'Escape') {
      setShowOptions(false);
      popperSourceElem?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();

      const currentIndex = Array.from(buttons).findIndex(
        button => button === document.activeElement,
      );
      const nextIndex = calculateNextIndex(currentIndex, buttons.length);

      buttons.forEach((btn, idx) => {
        btn.tabIndex = idx === nextIndex ? 0 : -1;
      });

      buttons[nextIndex].focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();

      const currentIndex = Array.from(buttons).findIndex(
        button => button === document.activeElement,
      );
      const nextIndex = calculatePreviousIndex(currentIndex, buttons.length);

      buttons.forEach((btn, idx) => {
        btn.tabIndex = idx === nextIndex ? 0 : -1;
      });

      buttons[nextIndex].focus();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.stopPropagation();
    }
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (!event.relatedTarget) {
      return;
    }

    const relatedTarget = event.relatedTarget;

    if (
      relatedTarget &&
      (menuRef.current?.contains(relatedTarget as Node) ||
        popperSourceElem === relatedTarget)
    ) {
      return;
    }

    hideOptions();
  };

  const onKeyDownMenu = (event: React.KeyboardEvent<HTMLElement>) => {
    if (
      !showOptions &&
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' ')
    ) {
      event.preventDefault();
      setShowOptions(true);
    }
  };

  const activeTextSize = getActiveTextSize(getEditorState());

  useEffect(() => {
    if (!showOptions || !menuRef.current) {
      return;
    }

    const items = textSizeBlockTypes.map(
      type => textSizeRefs.current[type].current as HTMLButtonElement,
    );

    const activeItemIndex = textSizeBlockTypes.findIndex(
      type => type === activeTextSize,
    );

    items.forEach((item, index) => {
      item.tabIndex = index === activeItemIndex ? 0 : -1;
    });

    if (activeItemIndex >= 0 && activeItemIndex < items.length) {
      items[activeItemIndex].focus();
    } else if (items.length > 0) {
      items[0].tabIndex = 0;
      items[0].focus();
    }
  }, [showOptions, activeTextSize]);

  return (
    <div className={styles.buttonContainer}>
      <Tooltip
        shouldShow={props.shouldShowTooltips}
        content={translations.textSizeSelected}
      >
        <button
          type="button"
          ref={node => {
            setPopperSourceElem(node);
            if (ref && 'current' in ref) {
              ref.current = node;
            }
          }}
          className={classnames(styles.button, styles.buttonTextContent, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onClick={onClickHandler}
          data-testid={TestIds.textSizeButton}
          tabIndex={tabIndex}
          aria-haspopup="true"
          aria-expanded={showOptions}
          aria-controls={`textSizeMenu_${rootId}`}
          onKeyDown={onKeyDownMenu}
          aria-label={`${translations.textSizeSelected}: ${textSizesSelected[activeTextSize]}`}
        >
          {textSizeButtons[activeTextSize]}
        </button>
      </Tooltip>
      <div
        ref={node => {
          setPopperTargetElem(node);
          menuContainerRef.current = node;
        }}
        style={{
          ...stylesPopper.popper,
          visibility: showOptions ? 'visible' : 'hidden',
          zIndex: '1',
        }}
        {...attributes.popper}
        role="menu"
        id={`textSizeMenu_${rootId}`}
        onKeyDown={onKeyDown}
      >
        <div
          ref={menuRef}
          className={classnames(styles.menuContainer, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onKeyDown={onKeyDown}
          onBlur={handleBlur}
        >
          {textSizeBlockTypes.map(textSize => (
            <Tooltip
              shouldShow={props.shouldShowTooltips}
              content={textSizeActions[textSize]}
              key={`tooltip_text_size_${textSize}`}
              position="right"
            >
              <button
                onClick={e => changeTextSize(e, textSize)}
                className={classnames(
                  styles.buttonTextContent,
                  textSize === activeTextSize && styles.buttonTextSelected,
                )}
                data-testid={`${TestIds.textSizeButton}-${textSize}`}
                tabIndex={-1}
                role="menuitemradio"
                aria-checked={activeTextSize === textSize}
                aria-label={textSizeActions[textSize]}
                ref={textSizeRefs.current[textSize]}
              >
                {textSizeButtons[textSize]}
              </button>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
});

export default StaticTextSizeButton;
